'use client'

import { useState, useEffect, useCallback } from 'react'
import { useLocale } from 'next-intl'
import { Link, useRouter } from '@/i18n/navigation'
import { useTheme } from 'next-themes'
import FinancialChart from '@/components/FinancialChart'
import FlagIcon from '@/components/FlagIcon'
import { fetchExchangeDetail, fetchChartData, type ExchangeDetail, type IndexQuote } from '@/services/marketApi'
import { t } from '@/i18n/translations'

interface Props {
  micCode: string
}

const periods = [
  { key: '1H', labelKey: 'period.weekly' },
  { key: '1A', labelKey: 'period.monthly' },
  { key: '1Y', labelKey: 'period.yearly' },
  { key: '5Y', labelKey: 'period.5year' },
]

function formatValue(val: number): string {
  const [int, dec] = val.toFixed(2).split('.')
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + dec
}

function formatPercent(val: number): string {
  return val.toFixed(2).replace('.', ',')
}

function Val({ val, suffix }: { val: number; suffix?: string }) {
  const color = val > 0 ? 'var(--chart-up)' : val < 0 ? 'var(--chart-down)' : 'var(--text-main)'
  const arrow = val > 0 ? ' \u2191' : val < 0 ? ' \u2193' : ''
  return (
    <span className="tabular-nums font-medium" style={{ color }}>
      {formatPercent(Math.abs(val))}{suffix || ''}{arrow}
    </span>
  )
}

function Skeleton({ w, h = 16, className = '' }: { w: number; h?: number; className?: string }) {
  return (
    <div
      className={`animate-pulse rounded ${className}`}
      style={{ width: w, height: h, backgroundColor: 'var(--border-color)' }}
    />
  )
}

function QuoteTable({ title, quotes, onRowClick, lang }: {
  title: string
  quotes: IndexQuote[]
  onRowClick: (q: IndexQuote) => void
  lang: string
}) {
  if (quotes.length === 0) return null

  return (
    <div
      className="rounded-xl border overflow-hidden mb-6"
      style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
    >
      <div className="p-4 sm:p-6 pb-2">
        <h2 className="text-lg font-bold m-0" style={{ color: 'var(--text-main)' }}>{title}</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse" style={{ minWidth: 600 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
              <th scope="col" className="px-4 sm:px-6 py-2 text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                {t('th.name', lang)}
              </th>
              <th scope="col" className="px-2 py-2 text-[11px] font-bold uppercase tracking-widest text-right" style={{ color: 'var(--text-muted)' }}>
                {t('th.last', lang)}
              </th>
              <th scope="col" className="px-2 py-2 text-[11px] font-bold uppercase tracking-widest text-right" style={{ color: 'var(--text-muted)' }}>%</th>
              <th scope="col" className="hidden md:table-cell px-2 py-2 text-[11px] font-bold uppercase tracking-widest text-right" style={{ color: 'var(--text-muted)' }}>
                {t('th.change', lang)}
              </th>
              <th scope="col" className="hidden md:table-cell px-2 py-2 text-[11px] font-bold uppercase tracking-widest text-right" style={{ color: 'var(--text-muted)' }}>
                {t('th.monthly', lang)}
              </th>
              <th scope="col" className="hidden md:table-cell px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-right" style={{ color: 'var(--text-muted)' }}>
                {t('th.yearly', lang)}
              </th>
            </tr>
          </thead>
          <tbody>
            {quotes.map((q, i) => {
              const up = q.changePercent >= 0
              const clr = up ? 'var(--chart-up)' : 'var(--chart-down)'
              return (
                <tr
                  key={q.symbol}
                  className="cursor-pointer transition-colors duration-100 hover:bg-[var(--bg-body)]"
                  style={{ borderBottom: i < quotes.length - 1 ? '1px solid var(--border-color)' : 'none' }}
                  onClick={() => onRowClick(q)}
                >
                  <td className="px-4 sm:px-6 py-2.5 md:py-3">
                    <div className="flex items-center gap-1.5 md:gap-2">
                      <span className="text-[10px] hidden md:inline" style={{ color: clr }}>{up ? '\u2191' : '\u2193'}</span>
                      <div>
                        <div className="text-[12px] md:text-[13px] font-bold leading-tight" style={{ color: 'var(--text-main)' }}>{q.symbol}</div>
                        <div className="text-[9px] mt-0.5 leading-tight uppercase tracking-wide hidden md:block" style={{ color: 'var(--text-muted)' }}>{q.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2.5 md:py-3 text-right">
                    <span className="text-[12px] md:text-[13px] font-semibold tabular-nums" style={{ color: clr }}>
                      {formatValue(q.close)} {up ? '\u2191' : '\u2193'}
                    </span>
                  </td>
                  <td className="px-2 py-2.5 md:py-3 text-right text-[12px] md:text-[13px]">
                    <Val val={q.changePercent} suffix="%" />
                  </td>
                  <td className="hidden md:table-cell px-2 py-3 text-right text-[13px]">
                    <span className="tabular-nums font-medium" style={{ color: clr }}>
                      {formatValue(Math.abs(q.diff))}
                    </span>
                  </td>
                  <td className="hidden md:table-cell px-2 py-3 text-right text-[13px]">
                    {q.monthly != null ? <Val val={q.monthly} suffix="%" /> : <span style={{ color: 'var(--text-muted)' }}>{'\u2014'}</span>}
                  </td>
                  <td className="hidden md:table-cell px-4 py-3 text-right text-[13px]">
                    {q.yearly != null ? <Val val={q.yearly} suffix="%" /> : <span style={{ color: 'var(--text-muted)' }}>{'\u2014'}</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function ExchangeDetailClient({ micCode }: Props) {
  const lang = useLocale()
  const router = useRouter()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  const [exchange, setExchange] = useState<ExchangeDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null)
  const [chartData, setChartData] = useState<{ time: number; value: number }[]>([])
  const [chartLoading, setChartLoading] = useState(true)
  const [period, setPeriod] = useState('1A')

  const loadExchange = useCallback(async () => {
    if (!micCode) return
    try {
      const data = await fetchExchangeDetail(micCode)
      if (!data) {
        setNotFound(true)
        setLoading(false)
        return
      }
      setExchange(data)
      if (data.indices.length > 0 && !selectedSymbol) {
        setSelectedSymbol(data.indices[0].symbol)
      }
    } catch (err) {
      console.error('Exchange detail fetch failed:', err)
    } finally {
      setLoading(false)
    }
  }, [micCode, selectedSymbol])

  useEffect(() => {
    loadExchange()
  }, [loadExchange])

  useEffect(() => {
    if (!selectedSymbol) {
      setChartLoading(false)
      return
    }
    setChartLoading(true)
    fetchChartData(selectedSymbol, period).then(data => {
      if (data && data.data.length > 0) {
        const mapped = data.data.map(p => ({
          time: new Date(p.time).getTime() / 1000,
          value: p.close,
        }))
        setChartData(mapped)
      } else {
        setChartData([])
      }
      setChartLoading(false)
    }).catch(() => {
      setChartData([])
      setChartLoading(false)
    })
  }, [selectedSymbol, period])

  if (notFound) {
    return (
      <main className="max-w-7xl mx-auto py-5 px-4">
        <div className="text-center py-24">
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-main)' }}>{t('exchange.notFound', lang)}</h1>
          <p className="mb-6" style={{ color: 'var(--text-muted)' }}>{t('exchange.notFoundDesc', lang)}</p>
          <button
            onClick={() => router.push('/')}
            className="px-6 py-2.5 rounded-lg font-medium transition-colors"
            style={{ backgroundColor: 'var(--accent-color)', color: '#fff' }}
          >
            {t('detail.backHome', lang)}
          </button>
        </div>
      </main>
    )
  }

  if (loading) {
    return (
      <main className="max-w-7xl mx-auto py-5 px-4 sm:px-6 lg:px-4">
        <div className="flex items-center gap-2 text-[13px] mb-6">
          <Skeleton w={60} h={14} />
          <span style={{ color: 'var(--text-muted)' }}>/</span>
          <Skeleton w={80} h={14} />
          <span style={{ color: 'var(--text-muted)' }}>/</span>
          <Skeleton w={120} h={14} />
        </div>
        <Skeleton w={300} h={32} className="mb-4" />
        <div className="rounded-xl border p-6 mb-6" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
          <div className="animate-pulse rounded-lg" style={{ height: 420, backgroundColor: 'var(--border-color)', opacity: 0.5 }} />
        </div>
      </main>
    )
  }

  if (!exchange) return null

  const handleIndexClick = (q: IndexQuote) => {
    router.push({ pathname: '/market/[id]', params: { id: q.symbol.toLowerCase() } })
  }

  const handleStockClick = (q: IndexQuote) => {
    router.push({ pathname: '/stock/[symbol]', params: { symbol: q.symbol } })
  }

  return (
    <main className="max-w-7xl mx-auto py-5 px-4 sm:px-6 lg:px-4">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-[13px] mb-6 overflow-hidden whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
        <Link href="/" className="hover:underline transition-colors shrink-0" style={{ color: 'var(--accent-color)' }}>
          {t('nav.home', lang)}
        </Link>
        <span className="select-none shrink-0">/</span>
        <span className="shrink-0">{t('exchange.exchanges', lang)}</span>
        <span className="select-none shrink-0">/</span>
        <span className="font-semibold truncate" style={{ color: 'var(--text-main)' }}>{exchange.name}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight m-0 flex items-center gap-3" style={{ color: 'var(--text-main)' }}>
            <FlagIcon code={exchange.countryCode} size={28} />
            {exchange.name}
            <span className="text-lg font-semibold" style={{ color: 'var(--text-muted)' }}>({exchange.micCode})</span>
          </h1>
          {exchange.countryName && (
            <p className="text-sm mt-1 m-0" style={{ color: 'var(--text-muted)' }}>{exchange.countryName}</p>
          )}
        </div>
      </div>

      {/* Exchange Info Cards */}
      <div
        className="rounded-xl border overflow-hidden mb-6 p-4 sm:p-6"
        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
          {[
            { label: t('exchange.currency', lang), value: exchange.currency },
            { label: t('exchange.timezone', lang), value: exchange.timezone },
            { label: t('exchange.tradingDays', lang), value: exchange.tradingDays || '\u2014' },
            { label: t('exchange.tradingHours', lang), value: exchange.tradingHours || '\u2014' },
            { label: t('exchange.dataAccess', lang), value: exchange.dataAccess },
          ].map(item => (
            <div key={item.label}>
              <div className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>{item.label}</div>
              <div className="text-sm font-bold" style={{ color: 'var(--text-main)' }}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Chart */}
      {exchange.indices.length > 0 && (
        <div
          className="rounded-xl border overflow-hidden mb-6"
          style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
        >
          {exchange.indices.length > 1 && (
            <div className="flex items-center gap-1 px-4 sm:px-6 pt-4 flex-wrap">
              {exchange.indices.map(idx => (
                <button
                  key={idx.symbol}
                  onClick={() => setSelectedSymbol(idx.symbol)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: selectedSymbol === idx.symbol ? 'var(--accent-color)' : 'transparent',
                    color: selectedSymbol === idx.symbol ? '#fff' : 'var(--text-muted)',
                    border: selectedSymbol === idx.symbol ? 'none' : '1px solid var(--border-color)',
                  }}
                >
                  {idx.symbol}
                </button>
              ))}
            </div>
          )}

          <div className="p-4 sm:p-6">
            {chartLoading ? (
              <div className="animate-pulse rounded-lg" style={{ height: 420, backgroundColor: 'var(--border-color)', opacity: 0.5 }} />
            ) : chartData.length === 0 ? (
              <div className="flex items-center justify-center rounded-lg" style={{ height: 420, backgroundColor: 'var(--border-color)', opacity: 0.3 }}>
                <span className="text-sm font-medium" style={{ color: 'var(--text-muted)', opacity: 1 }}>
                  {t('status.noData', lang)}
                </span>
              </div>
            ) : (
              <FinancialChart data={chartData} isDark={isDark} height={420} />
            )}
          </div>

          <div className="flex items-center gap-1 px-4 sm:px-6 pb-4">
            {periods.map(p => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{
                  backgroundColor: period === p.key ? 'var(--accent-color)' : 'transparent',
                  color: period === p.key ? '#fff' : 'var(--text-muted)',
                }}
              >
                {t(p.labelKey, lang)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Indices Table */}
      <QuoteTable
        title={t('exchange.indices', lang)}
        quotes={exchange.indices}
        onRowClick={handleIndexClick}
        lang={lang}
      />

      {/* Stocks Table */}
      <QuoteTable
        title={t('exchange.stocks', lang)}
        quotes={exchange.stocks}
        onRowClick={handleStockClick}
        lang={lang}
      />
    </main>
  )
}
